import { useMemo } from 'react';
import { Card } from '@/components/ui';
import { IncentiveKey } from '@/types';
import {
  formatDate,
  getIncentiveKey,
  sortIncentivesByApr,
} from '@/utils/common';
import { useIncentiveAPR } from '@/hooks/useIncentiveApr';
import { keccak256 } from 'ethers';

interface IncentiveSelectorProps {
  incentives: IncentiveKey[];
  selectedIncentive?: IncentiveKey;
  onIncentiveChange: (incentive: IncentiveKey | undefined) => void;
  poolData: {
    jocxPrice: number;
    tvl: number;
  };
}

export default function IncentiveSelector({
  incentives,
  selectedIncentive,
  onIncentiveChange,
}: IncentiveSelectorProps) {
  // Sort incentives by APR rate (higher first), with live incentives prioritized
  const sortedIncentives = useMemo(() => {
    return sortIncentivesByApr(incentives);
  }, [incentives]);

  // No incentives available
  if (incentives.length === 0) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-slate-700">
            Select Reward Program
          </label>
          <span className="text-xs text-slate-500">Choose which program to earn from</span>
        </div>
        <Card variant="compact" className="p-4 bg-slate-50 border-slate-200">
          <div className="text-center text-slate-500">
            <span className="text-lg font-medium">--</span>
            <p className="text-sm mt-1">No active programs available</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-slate-700">
          Select Reward Program
        </label>
        <span className="text-xs text-slate-500">Choose which program to earn from</span>
      </div>
      <div className="space-y-2">
        {sortedIncentives.map((incentive, idx) => (
          <IncentiveOptionCard
            key={incentive.id || idx}
            incentive={incentive}
            isSelected={selectedIncentive?.id === incentive.id}
            onSelect={() => onIncentiveChange(incentive)}
          />
        ))}
      </div>
    </div>
  );
}

interface IncentiveOptionCardProps {
  incentive: IncentiveKey;
  isSelected: boolean;
  onSelect: () => void;
}

const IncentiveOptionCard = ({
  incentive,
  isSelected,
  onSelect,
}: IncentiveOptionCardProps) => {
  const incentiveId = keccak256(getIncentiveKey(incentive));
  const apr = useIncentiveAPR(incentiveId);
  const isLive = Math.floor(Date.now() / 1000) <= Number(incentive.endTime);
  const reward = Number(incentive.reward) / 1e18;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
        isSelected
          ? 'border-blue-500 bg-blue-50'
          : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
      } ${!isLive ? 'opacity-60' : ''}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {/* Radio indicator */}
          <div
            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
              isSelected
                ? 'border-blue-500 bg-blue-500'
                : 'border-slate-300'
            }`}
          >
            {isSelected && (
              <div className="w-2 h-2 rounded-full bg-white" />
            )}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-slate-700">
                {reward.toLocaleString()} JOCX Rewards
              </span>
              {!isLive && (
                <span className="px-1.5 py-0.5 bg-slate-100 text-slate-500 text-xs rounded">
                  Ended
                </span>
              )}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {formatDate(incentive.startTime)} → {formatDate(incentive.endTime)}
            </div>
          </div>
        </div>
        <div className={`text-right ${isSelected ? 'text-blue-600' : 'text-slate-700'}`}>
          <div className="text-lg font-bold">
            {isLive ? `${apr}%` : '---'}
          </div>
          <div className="text-xs text-slate-500">APR</div>
        </div>
      </div>
    </button>
  );
};
